import { memo, useContext, useState } from "react";
import PropTypes from "prop-types";
import ReactModal from "react-modal";
import FloatingButton from "app/pages/.shared/Popover/FloatingButton";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";

import "./InfoPopover.scss";
import { InfoCircleStrokeIcon, CrossIcon } from "app/icons/navigation";

const InfoPopover = ({
	children,
	iconSize = 12,
	iconClassName = "",
	iconStyle = {},
	popoverPosition = "bottom",
	className = "",
	onOpenChange,
	dataTestId = "floating-button-ref",
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { resolution } = useContext(AppGlobalsContext);

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const handleOpenModal = () => {
		setIsModalOpen(true);
		if (typeof onOpenChange === "function") {
			onOpenChange(true);
		}
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		if (typeof onOpenChange === "function") {
			onOpenChange(false);
		}
	};

	// Isolate trigger clicks from parent interactive elements (e.g. <a>).
	const handleTriggerClick = e => {
		e.stopPropagation(); // block parent React handlers
		if (e.currentTarget.contains(e.target)) {
			e.preventDefault(); // block parent <a> navigation except portal clicks (popover/modal)
		}
	};

	// Mobile: fullscreen modal
	if (isMobile) {
		return (
			<div className={`info-popover ${className}`} onClick={handleTriggerClick}>
				<span
					className={`info-popover__trigger ${iconClassName}`}
					onClick={handleOpenModal}
					style={iconStyle}
					data-testid={dataTestId}
				>
					<InfoCircleStrokeIcon width={iconSize} height={iconSize} />
				</span>

				<ReactModal
					isOpen={isModalOpen}
					onRequestClose={handleCloseModal}
					className="info-popover__modal"
					overlayClassName="info-popover__overlay"
					ariaHideApp={false}
				>
					<button
						type="button"
						className="info-popover__modal-close"
						onClick={handleCloseModal}
						aria-label="Close"
					>
						<CrossIcon width={14} height={14} />
					</button>
					<div className="info-popover__modal-content">{children}</div>
				</ReactModal>
			</div>
		);
	}

	// Desktop: popover
	return (
		<FloatingButton
			floatingContainerClassName={`info-popover ${className}`}
			onClick={handleTriggerClick}
			referenceButtonClassName={`info-popover__trigger ${iconClassName}`}
			position={popoverPosition}
			referenceComponent={
				<InfoCircleStrokeIcon width={iconSize} height={iconSize} style={iconStyle} />
			}
			floatingContent={children}
			popoverClassName="info-popover__popover"
			zIndexValue={10}
			onOpenChange={onOpenChange}
			dataTestId={dataTestId}
		/>
	);
};

InfoPopover.propTypes = {
	children: PropTypes.node.isRequired,
	iconSize: PropTypes.number,
	iconClassName: PropTypes.string,
	iconStyle: PropTypes.object,
	popoverPosition: PropTypes.oneOf(["top", "bottom", "left", "right"]),
	className: PropTypes.string,
	onOpenChange: PropTypes.func,
	dataTestId: PropTypes.string,
};

export default memo(InfoPopover);
